import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProcedimentosElegiveis } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { CellClassParams, ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildDefaultColumnDef, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { ProcedimentosElegiveisService } from './procedimentos-elegiveis.service';
import { DecimalPipe, DatePipe } from '@angular/common';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.PROCEDIMENTOS_ELEGIVEIS';

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
  },
  {
    headerName: 'Classe',
    field: 'ds_classe',
  },
  {
    headerName: 'Qtde. ocorrências',
    field: 'qt_item',
    type: 'int',
    aggFunc: params => {
      const values = (params.values || []) as any[];
      if (!values.length) {
        return 0;
      }

      return values.reduce((acc, value) => acc + (Number(value) || 0), 0);
    },
  },
  {
    headerName: 'Tabela TUSS',
    field: 'cd_tabela_tuss',
  },
  {
    headerName: '066-Cód. Procedimento ou Item',
    field: 'cd_proced_item',
  },
  {
    headerName: 'Nome proced. item',
    field: 'nm_proced_item',
  },
  {
    headerName: 'Cód. Classe',
    field: 'cd_classe',
  },
];

@Component({
    templateUrl: './procedimentos-elegiveis.component.html',
    styleUrls: ['./procedimentos-elegiveis.component.scss'],
    providers: [ProcedimentosElegiveisService, DatePipe],
    standalone: false
})
export class ProcedimentosElegiveisComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];
  ibOdonto: number | null = null;
  tpOperadora: string | null = null;

  rowData: Row[];

  private classesComZeroEmAlgumaCompetencia = new Set<string>();

  dtUltimoProcessamento: string = null;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  private readonly TABELAS_ENVIO_INCORRETO = ['Tabela 98', 'Tabela 00'];

  private readonly CLASSES_ODONTOLOGICAS = [
    'Consulta Odontológica',
    'Procedimento preventivos de Odonto',
    'Procedimentos Preventivos em Saúde Bucal - Cárie',
    'Procedimentos Preventivos em Saúde Bucal - Periodontia',
    'Tratamento de Canal',
    'Raspagem supragengival',
  ];

  constructor(
    private permissaoService: PermissaoService,
    private service: ProcedimentosElegiveisService,
    private notificationService: NotificationService,
    private gridService: GridService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private competenciaPipe: CompetenciaPipe,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = this.store
      .select(LayoutSelectors.selectFullscreen)
      .pipe(map(fullscreen => (fullscreen ? 'calc(100vh - 130px)' : 'calc(100vh - 186px)')));
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;

        const rawIbOdonto = (operadora as any).ib_odonto;
        this.ibOdonto = rawIbOdonto != null ? Number(rawIbOdonto) : null;

        this.tpOperadora = (operadora as any).tp_operadora || null;

        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        processPivotResultColGroupDef: colGroupDef => {
          colGroupDef.headerName = this.competenciaPipe.transform(colGroupDef.headerName);
        },
        processPivotResultColDef: colDef => {
          const isQtItemCol = colDef.colId?.includes('qt_item') || colDef.field === 'qt_item';
          if (!isQtItemCol) {
            return;
          }

          colDef.valueFormatter = params => this.decimalPipe.transform(params.value ?? 0, '1.0-0');
        },
        defaultColDef: buildDefaultColumnDef({
          cellClassRules: {
            'cell-yellow': params => this.shouldHighlightCellYellow(params),
            'cell-red': params => this.isCellEnvioIncorreto(params) && this.toQtdeItens(params.value) > 0,
            'cell-neutral': params => this.isCellEnvioIncorreto(params) && this.toQtdeItens(params.value) === 0,
          },
          tooltipValueGetter: params => this.getColorTooltip(params),
        }),
        getRowClass: params => {
          const rowDataToCheck = this.buildRowDataToCheck(params);

          if (!rowDataToCheck) {
            return '';
          }

          if (this.shouldHighlightRowRed(rowDataToCheck)) {
            return 'row-red';
          }

          return '';
        },
        tooltipShowDelay: 1000,
        tooltipHideDelay: 3000,
        excelStyles: [
          { id: 'row-red', interior: { color: '#ff8181', pattern: 'Solid' } },
          { id: 'cell-yellow', interior: { color: '#fff2aa', pattern: 'Solid' } },
          { id: 'cell-red', interior: { color: '#ff8181', pattern: 'Solid' } },
          { id: 'cell-neutral', interior: { color: '#ffffff', pattern: 'Solid' } },
        ],
      },
    );
  }

  private isQtItemCell(params: { colDef?: any }): boolean {
    const colId = params.colDef?.colId || '';
    const field = params.colDef?.field || '';
    return colId.includes('qt_item') || field === 'qt_item';
  }

  private isCellEnvioIncorreto(params: { colDef?: ColDef; data?: Row }): boolean {
    if (!this.isQtItemCell(params)) {
      return false;
    }

    // Com pivot a tabela é uma das chaves da coluna; sem pivot, só a linha folha sabe a tabela.
    const tabelas: string[] = params.colDef?.pivotKeys?.length ? params.colDef.pivotKeys : [params.data?.cd_tabela_tuss];
    return tabelas.some(tabela => this.TABELAS_ENVIO_INCORRETO.includes(tabela));
  }

  private buildRowDataToCheck(params: { node: any; data: any }): any {
    let rowDataToCheck = null;

    if (params.node?.group) {
      const className = this.resolveClasseFromNode(params.node, params.node.key);
      if (className && typeof className === 'string') {
        // Linha agrupada normal (colapsada)
        const aggData = params.node?.aggData || {};
        const hasNonZeroItems = this.hasAnyPositiveQtItem(aggData);

        rowDataToCheck = {
          ds_classe: className,
          qt_item: hasNonZeroItems ? 1 : 0,
        };
      } else {
        // Filler row (groupHideOpenParents) - busca dados do primeiro filho
        const firstLeaf = params.node.allLeafChildren?.[0];
        rowDataToCheck = firstLeaf?.data ?? null;
      }
    } else {
      // Para linhas não agrupadas
      rowDataToCheck = params.data ? { ...params.data } : null;

      if (rowDataToCheck) {
        rowDataToCheck.ds_classe = this.resolveClasseFromNode(params.node, rowDataToCheck.ds_classe);

        if (rowDataToCheck.qt_item == null || rowDataToCheck.qt_item === '') {
          rowDataToCheck.qt_item = this.hasAnyPositiveQtItem(rowDataToCheck) ? 1 : 0;
        }
      }
    }

    return rowDataToCheck;
  }

  private shouldHighlightCellYellow(params: CellClassParams<Row>): boolean {
    if (!this.isQtItemCell(params) || this.isCellEnvioIncorreto(params)) {
      return false;
    }

    const rowDataToCheck = this.buildRowDataToCheck(params as any);
    if (!rowDataToCheck) {
      return false;
    }

    if (this.shouldHighlightRowRed(rowDataToCheck)) {
      return false;
    }

    const cellValue = this.toQtdeItens(params.value);
    return this.getHighlightReason(rowDataToCheck, cellValue)?.color === 'yellow';
  }

  private getColorTooltip(params: any): string {
    if (this.isCellEnvioIncorreto(params)) {
      return this.toQtdeItens(params.value) > 0
        ? 'Procedimento elegível informado em tabela TUSS 98 ou 00, indicando lançamento incorreto.'
        : '';
    }

    const rowDataToCheck = this.buildRowDataToCheck(params as any);
    if (!rowDataToCheck) {
      return '';
    }

    // Vermelho é a nível de linha
    const redCheck = this.getHighlightReason(rowDataToCheck);
    if (redCheck?.color === 'red') {
      return redCheck.message;
    }

    // Amarelo é a nível de célula (apenas colunas qt_item)
    if (this.isQtItemCell(params)) {
      const cellValue = this.toQtdeItens(params.value);
      const yellowCheck = this.getHighlightReason(rowDataToCheck, cellValue);
      if (yellowCheck?.color === 'yellow') {
        return yellowCheck.message;
      }
    }

    return '';
  }

  private normalizeClasse(value: any): string {
    return value == null ? '' : String(value).trim();
  }

  private isClasseCandidate(value: string): boolean {
    return (
      !!value &&
      (this.CLASSES_ODONTOLOGICAS.includes(value) ||
        this.classesComZeroEmAlgumaCompetencia.has(value) ||
        /[A-Za-zÀ-ÿ]/.test(value))
    );
  }

  private resolveClasseFromNode(node: any, fallbackClasse?: any): string {
    for (let groupNode = node; groupNode; groupNode = groupNode.parent) {
      if (groupNode.field === 'ds_classe' && groupNode.key != null) {
        return this.normalizeClasse(groupNode.key);
      }
    }

    const fallback = this.normalizeClasse(fallbackClasse);
    if (this.isClasseCandidate(fallback)) {
      return fallback;
    }

    let currentNode = node?.parent;
    while (currentNode) {
      const key = this.normalizeClasse(currentNode.key);
      if (this.isClasseCandidate(key)) {
        return key;
      }
      currentNode = currentNode.parent;
    }

    return '';
  }

  private toQtdeItens(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.replace(/\./g, '').replace(',', '.').trim();
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private hasAnyPositiveQtItem(source: any): boolean {
    if (!source) {
      return false;
    }

    return Object.keys(source).some(key => {
      if (!key.includes('qt_item')) {
        return false;
      }

      const rawValue = source[key];
      const numericValue =
        typeof rawValue === 'string' ? Number(rawValue.replace(/\./g, '').replace(',', '.')) : Number(rawValue);
      return !Number.isNaN(numericValue) && numericValue > 0;
    });
  }

  private shouldHighlightRowRed(rowData: any): boolean {
    return this.getHighlightReason(rowData)?.color === 'red';
  }

  private getHighlightReason(rowData: any, cellValue?: number): { color: 'red' | 'yellow'; message: string } | null {
    if (!rowData || this.ibOdonto === null) {
      return null;
    }

    const qtdeItens = this.toQtdeItens(rowData.qt_item);
    const classe = this.normalizeClasse(rowData.ds_classe);
    const tpOperadora = (this.tpOperadora || '').trim().toUpperCase();
    const isClasseOdontologica = this.CLASSES_ODONTOLOGICAS.includes(classe);

    if (qtdeItens === 0) {
      // Regra 1: ib_odonto = 1, tp_operadora = 'OD', qtde itens = 0, classe odontológica -> vermelho
      if (this.ibOdonto === 1 && tpOperadora === 'OD' && isClasseOdontologica) {
        return {
          color: 'red',
          message:
            'Procedimento odontológico elegível não identificado no período para operadora exclusivamente odontológica.',
        };
      }

      // Regra 2: ib_odonto = 1, tp_operadora = 'MH', qtde itens = 0, qualquer classe -> vermelho
      if (this.ibOdonto === 1 && tpOperadora === 'MH') {
        return {
          color: 'red',
          message:
            'Procedimento elegível não identificado no período para operadora Medico Hospitalar com cobertura odontológica.',
        };
      }

      // Regra 3: ib_odonto = 0, tp_operadora = 'MH', qtde itens = 0, classe NÃO odontológica -> vermelho
      if (this.ibOdonto === 0 && tpOperadora === 'MH' && !isClasseOdontologica) {
        return {
          color: 'red',
          message: 'Procedimento elegível não identificado no período para operadora Médico Hospitalar.',
        };
      }

      return null;
    }

    // Regras amarelas: usa o valor da célula individual se fornecido
    const cv = cellValue !== undefined ? cellValue : qtdeItens;

    // Regra 4: ib_odonto = 0, valor da célula > 0, classe odontológica -> amarelo
    if (this.ibOdonto === 0 && cv > 0 && isClasseOdontologica) {
      return {
        color: 'yellow',
        message: 'Procedimento odontológico identificado para operadora sem cobertura odontológica.',
      };
    }

    if (cv === 0 && this.classesComZeroEmAlgumaCompetencia.has(classe)) {
      // Regra 5: ib_odonto = 1, tp_operadora = 'MH', qualquer classe -> amarelo
      if (this.ibOdonto === 1 && tpOperadora === 'MH') {
        return {
          color: 'yellow',
          message:
            'Procedimento elegível não identificado na competência para operadora Medico Hospitalar com cobertura odontológica.',
        };
      }

      // Regra 6: ib_odonto = 1, tp_operadora = 'OD', classe odontológica -> amarelo
      if (this.ibOdonto === 1 && tpOperadora === 'OD' && isClasseOdontologica) {
        return {
          color: 'yellow',
          message:
            'Procedimento odontológico elegível não identificado na competência para operadora exclusivamente odontológica.',
        };
      }

      // Regra 7: ib_odonto = 0, tp_operadora = 'MH', classe NÃO odontológica -> amarelo
      if (this.ibOdonto === 0 && tpOperadora === 'MH' && !isClasseOdontologica) {
        return {
          color: 'yellow',
          message: 'Procedimento elegível não identificado na competência para operadora Médico Hospitalar.',
        };
      }
    }

    return null;
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]).subscribe({
      next: items => {
        if (!items?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          return;
        }

        this.classesComZeroEmAlgumaCompetencia = new Set(
          items.filter(r => (parseInt(r.qt_item as any) || 0) === 0).map(r => r.ds_classe),
        );

        this.rowData = items;
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });

    this.service.getUltimoProcessamento(this.operadora).subscribe({
      next: response => {
        const dateResponse = response?.items?.[0]?.dt_atualizacao_mon_guia;
        this.dtUltimoProcessamento = dateResponse ? this.datePipe.transform(+dateResponse, 'dd/MM/yyyy') : null;
      },
      error: error => this.notificationService.handleError(error),
    });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `procedimentos-elegiveis-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Procedimentos elegíveis',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface Row extends ProcedimentosElegiveis {}
